import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoClose,
  IoCar,
  IoSpeedometer,
  IoColorPalette,
  IoCheckmarkCircle,
  IoPricetag,
  IoCalendar,
  IoSettings,
  IoStatsChart,
  IoBarChart,
  IoImageSharp,
  IoCloudUpload,
  IoTrash,
} from "react-icons/io5";
import { BsFuelPump, BsGearFill, BsArrowRight } from "react-icons/bs";
import {
  FaCarSide,
  FaSuitcase,
  FaTools,
  FaHistory,
  FaUsers,
  FaIdCard,
  FaChartLine,
  FaMapPin,
} from "react-icons/fa";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ServiceHistoryTab from "../VehicleManagement/ServiceHistoryTab";
import VehicleInstancesPanel from "../VehicleManagement/VehicleInstancesPanel";
import { vehicleService, cars } from "../../services/api";

const CarDetailsModal = ({
  car,
  isOpen,
  onClose,
  onSave,
  onDelete,
  onSeasonSelect,
  isUpdating,
  isDeleting,
  settingSeasonalPrice,
  categories,
  editingSeasonalPrice,
  onEditingSeasonalPriceChange,
  seasonalPricing,
}) => {
  const [activeTab, setActiveTab] = useState("general");
  const [selectedVehicleId, setSelectedVehicleId] = useState(
    car.vehicleInstances?.length > 0 ? car.vehicleInstances[0]._id : null
  );
  const [vehicleStats, setVehicleStats] = useState({
    totalMileage: 0,
    averageMileage: 0,
    totalServiceCost: 0,
    serviceCount: 0,
    insuranceExpirations: [],
    nextServiceDue: null,
    rentalCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch vehicle stats
  useEffect(() => {
    if (car && car._id) {
      fetchVehicleStats();
    }
  }, [car]);

  const fetchVehicleStats = async () => {
    try {
      setLoading(true);
      // Here you would typically make an API call to get vehicle statistics
      // For now, we'll calculate some basic stats from the vehicle instances

      if (car.vehicleInstances && car.vehicleInstances.length > 0) {
        const totalMileage = car.vehicleInstances.reduce(
          (sum, v) => sum + (v.currentMileage || 0),
          0
        );
        const avgMileage = Math.round(
          totalMileage / car.vehicleInstances.length
        );

        // Count expiring insurances
        const expirations = car.vehicleInstances
          .filter((v) => v.insurance && v.insurance.endDate)
          .map((v) => ({
            vehicleId: v._id,
            licensePlate: v.licensePlate,
            expiryDate: new Date(v.insurance.endDate),
          }))
          .sort((a, b) => a.expiryDate - b.expiryDate);

        setVehicleStats({
          totalMileage,
          averageMileage: avgMileage,
          totalServiceCost: 0, // This would come from API
          serviceCount: 0, // This would come from API
          insuranceExpirations: expirations,
          nextServiceDue: null, // This would come from API
          rentalCount: 0, // This would come from API
        });
      }
    } catch (error) {
      console.error("Error fetching vehicle stats:", error);
    } finally {
      setLoading(false);
    }
  };

  // Image management functions
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Please select an image smaller than 5MB');
      return;
    }

    try {
      setIsUploadingImage(true);
      const response = await cars.updateImage(car._id, file);
      
      // Update the car object with new images
      const updatedCar = { ...car, images: response.images };
      onSave(updatedCar, true); // Pass true to indicate this is an image update
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageDelete = async (imageIndex) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      setIsDeletingImage(imageIndex);
      const response = await cars.deleteImage(car._id, imageIndex);
      
      // Update the car object with remaining images
      const updatedCar = { ...car, images: response.images };
      onSave(updatedCar, true); // Pass true to indicate this is an image update
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    } finally {
      setIsDeletingImage(null);
    }
  };

  if (!isOpen || !car) return null;
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.3 },
  };

  const tabVariants = {
    inactive: { opacity: 0, y: 10, display: "none" },
    active: { opacity: 1, y: 0, display: "block" },
    transition: { duration: 0.4 },
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 backdrop-blur-sm">
        <div className="flex items-center justify-center min-h-screen px-4 py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-5xl relative z-10"
          >
            {/* Header with Car Identity */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-t-xl p-6 relative">
              <button
                onClick={onClose}
                className="absolute right-4 top-4 p-2 rounded-full bg-blue-800/50 hover:bg-blue-700/50 text-white transition-colors"
              >
                <IoClose size={24} />
              </button>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center space-x-4 text-white">
                  <div className="bg-white/20 p-3 rounded-lg">
                    <IoCar className="text-3xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">
                      {car.make} {car.model}
                    </h2>
                    <p className="text-blue-100">
                      {car.year} • {car.category} •
                      {car.vehicleInstances && car.vehicleInstances.length > 0
                        ? ` ${car.vehicleInstances.length} vehicle instance${
                            car.vehicleInstances.length > 1 ? "s" : ""
                          }`
                        : " No vehicle instances"}
                    </p>
                  </div>
                </div>

                <div className="hidden sm:flex mt-4 sm:mt-0 space-x-2">
                  <div className="bg-white/10 px-3 py-1 rounded-full text-white text-sm font-medium">
                    Base Price: €{car.basePrice}/day
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      car.status === "available"
                        ? "bg-green-500 text-white"
                        : car.status === "maintenance"
                        ? "bg-yellow-500 text-white"
                        : "bg-red-500 text-white"
                    }`}
                  >
                    {car.status === "available"
                      ? "Available"
                      : car.status === "maintenance"
                      ? "Under Maintenance"
                      : "Unavailable"}
                  </div>
                </div>
              </div>

              {car.vehicleInstances &&
                car.vehicleInstances.some(
                  (v) =>
                    v.insurance &&
                    new Date(v.insurance.endDate) <
                      new Date(new Date().setDate(new Date().getDate() + 30))
                ) && (
                  <div className="mt-4 p-3 bg-red-600/80 text-white rounded-lg text-sm flex items-center">
                    <span className="bg-white p-1 rounded-full text-red-600 mr-2">
                      ⚠️
                    </span>{" "}
                    One or more vehicles have insurance expiring within 30 days
                    or already expired
                  </div>
                )}
            </div>

            {/* Vehicle Overview Cards - Show only in vehicles and service tabs */}
            {(activeTab === "vehicles" || activeTab === "service") && (
              <div className="px-6 pt-6 pb-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs font-medium text-blue-600 uppercase">
                          Total Vehicles
                        </p>
                        <h3 className="text-2xl font-bold mt-1">
                          {car.vehicleInstances?.length || 0}
                        </h3>
                      </div>
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <FaCarSide className="text-blue-600" />
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-blue-600">
                      {car.vehicleInstances?.filter(
                        (v) => v.condition === "good"
                      ).length || 0}{" "}
                      in good condition
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs font-medium text-green-600 uppercase">
                          Avg Mileage
                        </p>
                        <h3 className="text-2xl font-bold mt-1">
                          {vehicleStats.averageMileage.toLocaleString()} km
                        </h3>
                      </div>
                      <div className="bg-green-100 p-2 rounded-lg">
                        <IoSpeedometer className="text-green-600" />
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-green-600">
                      {vehicleStats.totalMileage.toLocaleString()} km total
                    </div>
                  </div>

                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs font-medium text-amber-600 uppercase">
                          Maintenance
                        </p>
                        <h3 className="text-2xl font-bold mt-1">
                          {vehicleStats.serviceCount || 0}
                        </h3>
                      </div>
                      <div className="bg-amber-100 p-2 rounded-lg">
                        <FaTools className="text-amber-600" />
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-amber-600">
                      €{(vehicleStats.totalServiceCost || 0).toLocaleString()}{" "}
                      total cost
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs font-medium text-red-600 uppercase">
                          Insurance Alert
                        </p>
                        <h3 className="text-2xl font-bold mt-1">
                          {vehicleStats.insuranceExpirations.filter(
                            (exp) =>
                              exp.expiryDate <
                              new Date(
                                new Date().setDate(new Date().getDate() + 30)
                              )
                          ).length || 0}
                        </h3>
                      </div>
                      <div className="bg-red-100 p-2 rounded-lg">
                        <FaIdCard className="text-red-600" />
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-red-600">
                      {vehicleStats.insuranceExpirations.length > 0
                        ? `Next expiry: ${vehicleStats.insuranceExpirations[0]?.expiryDate.toLocaleDateString()}`
                        : "No insurance data"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mt-4">
              <nav className="flex flex-wrap -mb-px px-6">
                <button
                  onClick={() => setActiveTab("general")}
                  className={`py-4 px-4 font-medium text-sm border-b-2 ${
                    activeTab === "general"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } transition-colors flex items-center`}
                >
                  <div className="flex items-center">
                    <IoSettings className="mr-2" />
                    General Settings
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("vehicles")}
                  className={`py-4 px-4 font-medium text-sm border-b-2 ${
                    activeTab === "vehicles"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } transition-colors flex items-center`}
                >
                  <div className="flex items-center">
                    <FaCarSide className="mr-2" />
                    Vehicle Management
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("service")}
                  className={`py-4 px-4 font-medium text-sm border-b-2 ${
                    activeTab === "service"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } transition-colors flex items-center`}
                >
                  <div className="flex items-center">
                    <FaTools className="mr-2" />
                    Service History
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("analytics")}
                  className={`py-4 px-4 font-medium text-sm border-b-2 ${
                    activeTab === "analytics"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } transition-colors flex items-center`}
                >
                  <div className="flex items-center">
                    <IoBarChart className="mr-2" />
                    Analytics
                  </div>
                </button>
              </nav>
            </div>

            {activeTab === "general" && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const features = formData
                    .get("features")
                    .split("\n")
                    .filter((f) => f.trim());
                  onSave({
                    make: formData.get("make"),
                    model: formData.get("model"),
                    year: Number(formData.get("year")),
                    category: formData.get("category"),
                    transmission: formData.get("transmission"),
                    fuelType: formData.get("fuelType"),
                    seats: Number(formData.get("seats")),
                    doors: Number(formData.get("doors")),
                    suitcases: Number(formData.get("suitcases")),
                    basePrice: Number(formData.get("basePrice")),
                    totalUnits: Number(formData.get("totalUnits")),
                    status: formData.get("status"),
                    description: formData.get("description"),
                    exteriorColor: formData.get("exteriorColor"),
                    interiorColor: formData.get("interiorColor"),
                    drivetrain: formData.get("drivetrain"),
                    airConditioner: formData.get("airConditioner") === "true",
                    features,
                    specifications: {
                      engine: formData.get("engine"),
                      power: formData.get("power"),
                      consumption: formData.get("consumption"),
                    },
                  });
                }}
                className="space-y-6 p-6"
              >
                {/* Image Management Section */}
                <motion.section
                  {...fadeInUp}
                  className="bg-white rounded-lg p-6 shadow-md border border-gray-100"
                >
                  <div className="flex items-center space-x-2 mb-4">
                    <IoImageSharp className="text-blue-900 text-xl" />
                    <h3 className="text-lg font-medium text-gray-900">Car Image</h3>
                  </div>
                  
                  
                  {/* Upload new image */}
                  <div className="border-t pt-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingImage}
                      className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isUploadingImage ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <IoCloudUpload className="mr-2 text-xl" />
                          <span>Upload New Image</span>
                        </>
                      )}
                    </button>
                    <p className="text-sm text-gray-500 mt-2">
                      Supported formats: JPEG, PNG, WebP. Max size: 5MB. First image will be used as primary.
                    </p>
                  </div>
                </motion.section>

                {/* Quick Stats */}
                <motion.div
                  {...fadeInUp}
                  className="grid grid-cols-2 md:grid-cols-4 gap-4"
                >
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 flex items-center space-x-3 border border-blue-200 hover:shadow-md transition-shadow">
                    <div className="bg-white p-2 rounded-lg text-blue-600">
                      <BsGearFill className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">
                        Transmission
                      </p>
                      <select
                        name="transmission"
                        defaultValue={car.transmission}
                        required
                        className="font-medium bg-transparent border-none p-0 focus:ring-0 text-blue-800"
                      >
                        <option value="manual">Manual</option>
                        <option value="automatic">Automatic</option>
                      </select>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 flex items-center space-x-3 border border-green-200 hover:shadow-md transition-shadow">
                    <div className="bg-white p-2 rounded-lg text-green-600">
                      <BsFuelPump className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">
                        Fuel Type
                      </p>
                      <select
                        name="fuelType"
                        defaultValue={car.fuelType}
                        required
                        className="font-medium bg-transparent border-none p-0 focus:ring-0 text-green-800"
                      >
                        <option value="gasoline">Gasoline</option>
                        <option value="diesel">Diesel</option>
                        <option value="electric">Electric</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 flex items-center space-x-3 border border-amber-200 hover:shadow-md transition-shadow">
                    <div className="bg-white p-2 rounded-lg text-amber-600">
                      <FaCarSide className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Seats</p>
                      <input
                        type="number"
                        name="seats"
                        defaultValue={car.seats}
                        required
                        min="1"
                        max="15"
                        className="font-medium bg-transparent border-none p-0 w-20 focus:ring-0 text-amber-800"
                      />
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 flex items-center space-x-3 border border-purple-200 hover:shadow-md transition-shadow">
                    <div className="bg-white p-2 rounded-lg text-purple-600">
                      <FaSuitcase className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 font-medium">
                        Luggage
                      </p>
                      <input
                        type="number"
                        name="suitcases"
                        defaultValue={car.suitcases}
                        required
                        min="0"
                        className="font-medium bg-transparent border-none p-0 w-20 focus:ring-0 text-purple-800"
                      />
                    </div>
                  </div>
                </motion.div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Specifications */}
                  <motion.section
                    {...fadeInUp}
                    className="bg-white rounded-lg p-6 shadow-md border border-gray-100"
                  >
                    <div className="flex items-center space-x-2 mb-4">
                      <IoSpeedometer className="text-blue-900 text-xl" />
                      <h3 className="text-lg font-medium text-gray-900">
                        Specifications
                      </h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Make
                        </label>
                        <input
                          type="text"
                          name="make"
                          defaultValue={car.make}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Model
                        </label>
                        <input
                          type="text"
                          name="model"
                          defaultValue={car.model}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Year
                        </label>
                        <input
                          type="number"
                          name="year"
                          defaultValue={car.year}
                          required
                          min="1900"
                          max="2025"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Category
                        </label>
                        <select
                          name="category"
                          defaultValue={car.category}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Drivetrain
                        </label>
                        <select
                          name="drivetrain"
                          defaultValue={car.drivetrain}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="2WD">2WD</option>
                          <option value="4WD">4WD</option>
                          <option value="AWD">AWD</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Air Conditioner
                        </label>
                        <select
                          name="airConditioner"
                          defaultValue={car.airConditioner?.toString()}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Doors *
                        </label>
                        <input
                          type="number"
                          name="doors"
                          defaultValue={car.doors}
                          required
                          min="1"
                          max="5"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </motion.section>

                  {/* Colors & Features */}
                  <motion.section
                    {...fadeInUp}
                    className="bg-white rounded-lg p-6 shadow-md border border-gray-100"
                  >
                    <div className="flex items-center space-x-2 mb-4">
                      <IoColorPalette className="text-blue-900 text-xl" />
                      <h3 className="text-lg font-medium text-gray-900">
                        Colors & Features
                      </h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Exterior Color
                        </label>
                        <input
                          type="text"
                          name="exteriorColor"
                          defaultValue={car.exteriorColor}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Interior Color
                        </label>
                        <input
                          type="text"
                          name="interiorColor"
                          defaultValue={car.interiorColor}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Features (one per line)
                        </label>
                        <textarea
                          name="features"
                          defaultValue={car.features?.join("\n") || ""}
                          rows="4"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        ></textarea>
                      </div>
                    </div>
                  </motion.section>
                </div>
                {/* Engine Specifications */}
                <motion.section
                  {...fadeInUp}
                  className="bg-white rounded-lg p-6 shadow-md border border-gray-100"
                >
                  <div className="flex items-center space-x-2 mb-4 p-4">
                    <IoSpeedometer className="text-blue-900 text-xl" />
                    <h3 className="text-lg font-medium text-gray-900">
                      Engine Specifications
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Engine
                      </label>
                      <input
                        type="text"
                        name="engine"
                        defaultValue={car.specifications?.engine}
                        placeholder="e.g. 2.0L 4-cylinder"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Power
                      </label>
                      <input
                        type="text"
                        name="power"
                        defaultValue={car.specifications?.power}
                        placeholder="e.g. 150 HP"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fuel Consumption
                      </label>
                      <input
                        type="text"
                        name="consumption"
                        defaultValue={car.specifications?.consumption}
                        placeholder="e.g. 7.5L/100km"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </motion.section>{" "}
                {/* Pricing & Availability */}
                <motion.section
                  {...fadeInUp}
                  className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg p-6 text-white shadow-lg"
                >
                  <div className="flex items-center space-x-2 mb-6">
                    <div className="bg-white/20 p-2 rounded-full">
                      <IoPricetag className="text-white text-xl" />
                    </div>
                    <h3 className="text-lg font-medium">
                      Pricing & Availability
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <label className="block text-sm font-medium text-green-100 mb-2">
                        Base Price (€/day)
                      </label>
                      <div className="relative">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-white">
                          €
                        </span>
                        <input
                          type="number"
                          name="basePrice"
                          defaultValue={car.basePrice}
                          required
                          min="0"
                          step="0.01"
                          className="mt-1 block w-full pl-8 pr-3 py-2 rounded-md border-0 bg-white/20 text-white placeholder-green-200 focus:ring-2 focus:ring-white"
                        />
                      </div>
                      <p className="mt-2 text-xs text-green-100">
                        Standard daily rate for this vehicle
                      </p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <label className="block text-sm font-medium text-green-100 mb-2">
                        Total Units
                      </label>
                      <input
                        type="number"
                        name="totalUnits"
                        defaultValue={car.totalUnits}
                        required
                        min="0"
                        className="mt-1 block w-full rounded-md border-0 bg-white/20 text-white placeholder-green-200 focus:ring-2 focus:ring-white"
                      />
                      <p className="mt-2 text-xs text-green-100">
                        Number of vehicles in the fleet
                      </p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <label className="block text-sm font-medium text-green-100 mb-2">
                        Status
                      </label>
                      <select
                        name="status"
                        defaultValue={car.status}
                        className="mt-1 block w-full rounded-md border-0 bg-white/20 text-white focus:ring-2 focus:ring-white"
                      >
                        <option value="available">Available</option>
                        <option value="maintenance">Under Maintenance</option>
                        <option value="unavailable">Unavailable</option>
                      </select>
                      <p className="mt-2 text-xs text-green-100">
                        Current rental availability
                      </p>
                    </div>
                  </div>
                  {/* <div className="mt-6 p-4 bg-white/10 rounded-lg backdrop-blur-sm">
                    <h4 className="text-sm font-medium text-white mb-2">
                      Potential Monthly Revenue
                    </h4>
                    <div className="flex items-end justify-between">
                      <span className="text-2xl font-bold text-white">
                        €
                        {(car.basePrice * 30 * car.totalUnits).toLocaleString()}
                      </span>
                      <span className="text-sm text-green-100">
                        Based on full utilization
                      </span>
                    </div>
                  </div> */}
                </motion.section>
                {/* Seasonal Pricing */}
                <motion.section
                  {...fadeInUp}
                  className="bg-white rounded-lg p-6 shadow-md border border-gray-100"
                >
                  <div className="flex items-center space-x-2 mb-4">
                    <IoCalendar className="text-blue-900 text-xl" />
                    <h3 className="text-lg font-medium text-gray-900">
                      Seasonal Pricing
                    </h3>
                  </div>

                  {/* Predefined Seasons */}
                  <div className="space-y-4 mb-6">
                    <label className="block text-sm font-medium text-gray-700">
                      Quick Season Setup
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button
                        type="button"
                        onClick={() =>
                          onSeasonSelect(car._id, car.basePrice, "low")
                        }
                        className="px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={settingSeasonalPrice?.carId === car._id}
                      >
                        {settingSeasonalPrice?.carId === car._id &&
                        settingSeasonalPrice?.season === "low" ? (
                          <div className="flex items-center justify-center">
                            <LoadingSpinner size="sm" className="mr-2" />
                            <span>Setting Low Season...</span>
                          </div>
                        ) : (
                          "Set Low Season"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          onSeasonSelect(car._id, car.basePrice, "medium")
                        }
                        className="px-4 py-3 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={settingSeasonalPrice?.carId === car._id}
                      >
                        {settingSeasonalPrice?.carId === car._id &&
                        settingSeasonalPrice?.season === "medium" ? (
                          <div className="flex items-center justify-center">
                            <LoadingSpinner size="sm" className="mr-2" />
                            <span>Setting Medium Season...</span>
                          </div>
                        ) : (
                          "Set Medium Season"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          onSeasonSelect(car._id, car.basePrice, "high")
                        }
                        className="px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={settingSeasonalPrice?.carId === car._id}
                      >
                        {settingSeasonalPrice?.carId === car._id &&
                        settingSeasonalPrice?.season === "high" ? (
                          <div className="flex items-center justify-center">
                            <LoadingSpinner size="sm" className="mr-2" />
                            <span>Setting High Season...</span>
                          </div>
                        ) : (
                          "Set High Season"
                        )}
                      </button>
                    </div>
                    <div className="text-sm text-gray-500 space-y-1 mt-2">
                      <p>Low Season: Apr 1 - May 15, Sep 21 - Oct 30</p>
                      <p>Medium Season: May 16 - Jun 15, Sep 6 - Sep 20</p>
                      <p>High Season: Jun 16 - Sep 5</p>
                    </div>
                  </div>

                  {/* Custom Date Price */}
                  <div className="border-t pt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-4">
                      Set Custom Date Price
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <DatePicker
                          selected={editingSeasonalPrice?.date}
                          onChange={(date) =>
                            onEditingSeasonalPriceChange({
                              ...editingSeasonalPrice,
                              date,
                            })
                          }
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholderText="Select date"
                          minDate={new Date()}
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          value={editingSeasonalPrice?.price || ""}
                          onChange={(e) =>
                            onEditingSeasonalPriceChange({
                              ...editingSeasonalPrice,
                              price: Number(e.target.value),
                            })
                          }
                          placeholder="Price per day"
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Current Seasonal Prices */}
                  {seasonalPricing[car._id]?.length > 0 && (
                    <div className="border-t pt-6 mt-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-4">
                        Current Seasonal Prices
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {seasonalPricing[car._id]
                          .sort((a, b) => new Date(a.date) - new Date(b.date))
                          .reduce((groups, pricing) => {
                            const existingGroup = groups.find(
                              (g) => g.price === pricing.dynamicPrice
                            );
                            if (existingGroup) {
                              existingGroup.dates.push(new Date(pricing.date));
                            } else {
                              groups.push({
                                price: pricing.dynamicPrice,
                                dates: [new Date(pricing.date)],
                              });
                            }
                            return groups;
                          }, [])
                          .map((group, idx) => (
                            <div
                              key={idx}
                              className="bg-gray-50 p-4 rounded-lg"
                            >
                              <div className="flex justify-between items-center mb-2">
                                <div className="text-sm font-medium text-gray-900">
                                  €{group.price}/day
                                </div>
                                <div className="text-xs text-gray-500">
                                  {group.dates.length} day(s)
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {group.dates.map((date, i) => (
                                  <div
                                    key={i}
                                    className="text-xs bg-white px-2 py-1 rounded border"
                                  >
                                    {date.toLocaleDateString("el-GR", {
                                      day: "2-digit",
                                      month: "2-digit",
                                    })}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </motion.section>
                {/* Actions */}
                <motion.div
                  {...fadeInUp}
                  className="sticky bottom-0 bg-white pt-4 pb-4 border-t mt-8"
                >
                  <div className="flex justify-between gap-4">
                    <button
                      type="button"
                      onClick={() => onDelete(car._id)}
                      disabled={isDeleting}
                      className="px-6 py-2 text-white bg-red-600 hover:bg-red-700 rounded-full shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                    >
                      {isDeleting ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        "Delete Car"
                      )}
                    </button>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdating}
                        className="px-6 py-2 text-white bg-blue-900 hover:bg-blue-800 rounded-full shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                      >
                        {isUpdating ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          "Save Changes"
                        )}{" "}
                      </button>
                    </div>{" "}
                  </div>
                </motion.div>
              </form>
            )}

            {/* Vehicle Management Tab */}
            {activeTab === "vehicles" && (
              <motion.div
                key="vehicles-tab"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-y-auto max-h-[75vh]"
              >
                <div className="p-6">
                  <VehicleInstancesPanel
                    car={car}
                    token={localStorage.getItem("adminToken")}
                  />
                </div>
              </motion.div>
            )}

            {/* Service History Tab */}
            {activeTab === "service" && (
              <motion.div
                key="service-tab"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-y-auto max-h-[75vh]"
              >
                <div className="p-6">
                  <ServiceHistoryTab
                    car={car}
                    selectedVehicleId={selectedVehicleId}
                    onVehicleSelect={setSelectedVehicleId}
                    token={localStorage.getItem("adminToken")}
                  />
                </div>
              </motion.div>
            )}

            {/* Analytics Tab */}
            {activeTab === "analytics" && (
              <motion.div
                key="analytics-tab"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-y-auto max-h-[75vh]"
              >
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <IoBarChart className="mr-2 text-blue-600" />
                    Fleet Analytics
                  </h3>

                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">
                          Fleet Utilization
                        </h4>
                        <div className="text-3xl font-bold text-gray-800">
                          {car.vehicleInstances
                            ? Math.round(
                                (car.vehicleInstances.filter(
                                  (v) => v.status === "rented"
                                ).length /
                                  car.vehicleInstances.length) *
                                  100
                              )
                            : 0}
                          %
                        </div>
                        <div className="mt-1 text-sm text-gray-500">
                          Current utilization rate
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">
                          Revenue
                        </h4>
                        <div className="text-3xl font-bold text-gray-800">
                          €{(car.basePrice * 30).toLocaleString()}
                        </div>
                        <div className="mt-1 text-sm text-gray-500">
                          Potential monthly revenue
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">
                          Maintenance Cost
                        </h4>
                        <div className="text-3xl font-bold text-gray-800">
                          €
                          {(
                            vehicleStats.totalServiceCost || 0
                          ).toLocaleString()}
                        </div>
                        <div className="mt-1 text-sm text-gray-500">
                          Total maintenance costs
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">
                          Model Popularity
                        </h4>
                        <div className="text-3xl font-bold text-gray-800">
                          {vehicleStats.rentalCount || 0}
                        </div>
                        <div className="mt-1 text-sm text-gray-500">
                          Total rental count
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                      <h4 className="font-medium text-gray-700 mb-4">
                        Seasonal Price Analysis
                      </h4>
                      <p className="text-sm text-gray-500 mb-4">
                        This chart shows the price variations throughout the
                        year.
                      </p>

                      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100">
                        <div className="text-center text-gray-500">
                          <FaChartLine className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                          <p>Interactive chart would be displayed here.</p>
                          <p className="text-xs mt-1">
                            Showing price variations across seasons
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                      <h4 className="font-medium text-gray-700 mb-4">
                        Vehicle Availability
                      </h4>
                      <p className="text-sm text-gray-500 mb-4">
                        Current status of each vehicle in the fleet.
                      </p>

                      <div className="space-y-3">
                        {car.vehicleInstances?.map((vehicle, index) => (
                          <div
                            key={vehicle._id || index}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center">
                              <div
                                className={`w-3 h-3 rounded-full mr-3 ${
                                  vehicle.status === "available"
                                    ? "bg-green-500"
                                    : vehicle.status === "maintenance"
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                                }`}
                              ></div>
                              <span className="font-medium">
                                {vehicle.licensePlate}
                              </span>
                            </div>
                            <span className="text-sm text-gray-500">
                              {vehicle.currentMileage || 0} km
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                    <h4 className="font-medium text-gray-700 mb-4">
                      Maintenance Schedule
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Upcoming and past maintenance events.
                    </p>

                    <div className="space-y-4">
                      {vehicleStats.serviceCount > 0 ? (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h5 className="font-medium text-gray-700">
                            Scheduled Maintenance
                          </h5>
                          <p className="text-sm text-gray-500 mt-1">
                            No upcoming maintenance scheduled.
                          </p>
                        </div>
                      ) : (
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                          <h5 className="font-medium text-blue-700">
                            Recommended Action
                          </h5>
                          <p className="text-sm text-blue-600 mt-1">
                            No service records found. Consider scheduling
                            initial maintenance checkups for all vehicles.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default CarDetailsModal;
